import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import type { WardrobeParts, PanelPart } from '~/context/wardrobePartsContext'
import type { WardrobeColumnConfiguration } from '~/types/wardrobeConfigurationTypes'

/**
 * Request body for POST /api/pricing/quote (backend pricing module).
 * All dimensions are in millimetres. See dulap.md/api/docs-json.
 */
interface PricingQuoteRequest {
  sku: string
  chipboardId?: string
  width: number
  height: number
  depth: number
  parts: [number, number][]
  zones: Record<string, number | boolean | string>[]
  sections: number
  /** Product-global fitting selections; backend registers each as `option.<key>`. */
  options: Record<string, number | boolean | string>
}

/** One cost group in the quote breakdown. `groupSku` is the stable i18n key. */
interface PricingQuoteBreakdownItem {
  groupId: string
  groupSku: string
  groupName: string
  amount: number
}

/** Response body from POST /api/pricing/quote. */
export interface PricingQuote {
  currency: string
  total: number
  aggregates: Record<string, number>
  breakdown: PricingQuoteBreakdownItem[]
}

/**
 * The exact request body sent to /api/pricing/quote paired with the response it
 * produced. Stored on a cart item so checkout can forward the same payload the
 * pricing endpoint received plus its answer — nothing reconstructed.
 */
export interface PricingQuoteResult {
  request: PricingQuoteRequest
  response: PricingQuote
}

const CM_TO_MM = 10
const mm = (cm: number): number => Math.round(cm * CM_TO_MM)

/**
 * Flatten panels into [width, height] mm pairs, expanding quantity. Accepts the
 * named WardrobeParts buckets or an already-flat panel list (e.g. stand).
 */
function partsToPairs(parts: WardrobeParts | PanelPart[]): [number, number][] {
  const panels: PanelPart[] = Array.isArray(parts)
    ? parts
    : [
        parts.sidePanels.left,
        parts.sidePanels.right,
        parts.topPanel,
        parts.plinth,
        parts.backPanel,
        ...parts.columnDividers,
        ...parts.doors,
        ...parts.interiorPanels,
      ]

  const pairs: [number, number][] = []
  for (const panel of panels) {
    const count = Math.max(1, Math.round(panel.quantity))
    for (let i = 0; i < count; i++) {
      pairs.push([mm(panel.width), mm(panel.height)])
    }
  }
  return pairs
}

/** Zone types whose price depends on the column width (hanging rod length). */
const WIDTH_DEPENDENT_ZONE_TYPES = new Set<string>([
  'HANGING_LONG',
  'HANGING_TOP_PREMIUM',
])

/** Map column configurations to flat zone records the backend aggregates by key. */
function configsToZones(
  configs?: WardrobeColumnConfiguration[],
  columnWidths?: number[]
): Record<string, number | boolean | string>[] {
  if (!configs) return []

  const zones: Record<string, number | boolean | string>[] = []
  configs.forEach((col, colIndex) => {
    for (const zone of col.zones) {
      const record: Record<string, number | boolean | string> = {
        type: zone.type,
        height: zone.height,
        shelves: zone.shelfCount ?? 0,
        drawers: zone.drawerCount ?? 0,
        hasShelfAtTop: zone.addShelfAtTheTop ?? false,
      }
      // Hanging long zones price by rod length: include the column width.
      const colWidth = columnWidths?.[colIndex]
      if (WIDTH_DEPENDENT_ZONE_TYPES.has(zone.type) && colWidth != null) {
        record.width = colWidth
      }
      zones.push(record)
    }
  })
  return zones
}

export interface BuildPricingQuoteArgs {
  sku: string
  chipboardId?: string
  width: number
  height: number
  depth: number
  handleType: string
  doorsNumber: number
  sections: number
  parts: WardrobeParts | PanelPart[]
  columnConfigurations?: WardrobeColumnConfiguration[]
  /** Per-column widths (cm); used to price width-dependent zones (hanging rods). */
  columnWidths?: number[]
  /** Pre-built zone records (stand); overrides columnConfigurations mapping. */
  zones?: Record<string, number | boolean | string>[]
}

function buildPricingQuote(args: BuildPricingQuoteArgs): PricingQuoteRequest {
  return {
    sku: args.sku,
    ...(args.chipboardId ? { chipboardId: args.chipboardId } : {}),
    width: mm(args.width),
    height: mm(args.height),
    depth: mm(args.depth),
    sections: Math.max(1, Math.round(args.sections)),
    parts: partsToPairs(args.parts),
    zones: args.zones ?? configsToZones(args.columnConfigurations, args.columnWidths),
    options: { handleType: args.handleType, doorsNumber: args.doorsNumber },
  }
}

/**
 * One-shot pricing quote (no React). Used by the catalog cards to price each
 * preset/colour via the same backend endpoint the configurator uses. Resolves
 * to null on failure so callers can fall back to a static price.
 */
export async function fetchPricingQuote(
  args: BuildPricingQuoteArgs,
  signal?: AbortSignal
): Promise<PricingQuote | null> {
  try {
    const { data } = await axios.post<PricingQuote>(
      '/api/pricing/quote',
      buildPricingQuote(args),
      { signal }
    )
    return data
  } catch (err) {
    if (axios.isCancel(err)) return null
    if (import.meta.env.DEV) {
      console.warn('[pricing] fetchPricingQuote failed', err)
    }
    return null
  }
}

/**
 * Fire a pricing-quote request on every configuration change and return the
 * latest quote. Debounced so a burst of slider changes collapses into a single
 * request. Stale responses (config changed mid-flight) are discarded.
 */
export function usePricingQuoteSync(
  args: BuildPricingQuoteArgs
): PricingQuoteResult | null {
  const latest = useRef(args)
  // Keep the ref pointing at the freshest args without writing during render.
  useEffect(() => {
    latest.current = args
  })

  const [result, setResult] = useState<PricingQuoteResult | null>(null)

  // Re-fire whenever the serialized payload changes.
  const payload = buildPricingQuote(args)
  const key = JSON.stringify(payload)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      // Capture the exact body sent so it can be paired with the response and,
      // eventually, forwarded verbatim at checkout.
      const request = buildPricingQuote(latest.current)
      axios
        .post<PricingQuote>('/api/pricing/quote', request)
        .then((res) => {
          if (!cancelled) setResult({ request, response: res.data })
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.warn('[pricing] /api/pricing/quote failed', err)
          }
        })
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [key])

  return result
}
